import type { IconNameExtended as IconName } from "@/components/library/Icon";
import { resolveRouteFromPathname } from "@/data/routeRegistry";
import { Permissions, RoleData } from "@/enums/user";

/** Pages the launcher can name. `page` is any other registered route; unknown paths get no page. */
export type LauncherPageKey = "dashboard" | "campaign" | "script" | "contactList" | "contact" | "call" | "page";

export type LauncherStarter = {
  /** Copy lives at `ask:launcher.starters.<id>`. */
  id: string;
  icon: IconName;
  /** A `change` waits for approval, so the preview tags it "Asks first". */
  kind: "read" | "change";
  /** Hidden unless the person holds this permission, since Gail would only be refused. */
  requires?: { data: RoleData; level: Permissions };
};

export type LauncherPage = {
  key: LauncherPageKey;
  /** At most one read and one change, read first. Empty when the page has no starters. */
  starters: readonly LauncherStarter[];
};

const read = (id: string, icon: IconName): LauncherStarter => ({ id, icon, kind: "read" });
const change = (id: string, icon: IconName, data: RoleData, level: Permissions): LauncherStarter => ({
  id,
  icon,
  kind: "change",
  requires: { data, level },
});

type PagePattern = { pattern: RegExp; key: LauncherPageKey; starters?: readonly LauncherStarter[] };

// Anchored with a single bounded segment class, so matching stays linear on any pathname.
const PAGE_PATTERNS: readonly PagePattern[] = [
  {
    pattern: /^\/$/,
    key: "dashboard",
    starters: [
      read("dashboardRead", "chart-line"),
      change("dashboardChange", "circle-play", RoleData.Campaigns, Permissions.Create),
    ],
  },
  {
    pattern: /^\/outbound\/campaigns\/[^/]+\/results$/,
    key: "campaign",
    starters: [
      read("campaignRead", "chart-line"),
      change("campaignChange", "pencil", RoleData.Scripts, Permissions.Update),
    ],
  },
  { pattern: /^\/outbound\/campaigns\/[^/]+$/, key: "campaign" },
  {
    pattern: /^\/inbound\/scripts\/[^/]+$/,
    key: "script",
    starters: [
      read("scriptRead", "file-lines"),
      change("scriptChange", "pencil", RoleData.Inbound, Permissions.Update),
    ],
  },
  { pattern: /^\/outbound\/scripts\/share(\/[^/]+)?$/, key: "page" },
  {
    pattern: /^\/outbound\/scripts\/[^/]+$/,
    key: "script",
    starters: [
      read("scriptRead", "file-lines"),
      change("scriptChange", "pencil", RoleData.Scripts, Permissions.Update),
    ],
  },
  { pattern: /^\/outbound\/scripts\/(text|email|blaster)\/[^/]+$/, key: "script" },
  {
    pattern: /^\/communications\/contact-lists\/[^/]+$/,
    key: "contactList",
    starters: [
      read("contactListRead", "users"),
      change("contactListChange", "circle-play", RoleData.Campaigns, Permissions.Create),
    ],
  },
  { pattern: /^\/communications\/contacts\/[^/]+$/, key: "contact" },
  { pattern: /^\/calls\/[^/]+$/, key: "call" },
];

const trimTrailingSlashes = (pathname: string) => {
  let end = pathname.length;
  while (end > 1 && pathname.charCodeAt(end - 1) === 47 /* "/" */) end--;
  return pathname.slice(0, end) || "/";
};

/** What the launcher calls the current page and which starters it offers there. */
export function resolveLauncherPage(pathname: string): LauncherPage | null {
  const path = trimTrailingSlashes(pathname);
  const match = PAGE_PATTERNS.find(({ pattern }) => pattern.test(path));
  if (match) return { key: match.key, starters: match.starters ?? [] };
  return resolveRouteFromPathname(path) ? { key: "page", starters: [] } : null;
}
